// DraggableWindow.js

// Below this the window is part of the document flow, not a floating window,
// so there is nothing to drag. Matches the breakpoint in main.css.
const FLOATING = "(min-width: 45rem)";

// However far it is thrown, this much of the window stays on screen — enough
// to grab the title bar and pull it back.
const KEEP_VISIBLE = 48;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export class DraggableWindow {
  constructor(element) {
    this.element = element;
    this.handle = element.querySelector(".window-top") || element;
    this.floating = window.matchMedia(FLOATING);
    this.pointerId = null;
    this.grip = null;
    this.pinned = false;

    this.handle.addEventListener("pointerdown", this.pointerDown.bind(this));
    this.handle.addEventListener("pointermove", this.pointerMove.bind(this));
    this.handle.addEventListener("pointerup", this.pointerUp.bind(this));
    this.handle.addEventListener("pointercancel", this.pointerUp.bind(this));
    this.floating.addEventListener("change", this.layoutChanged.bind(this));
  }

  pointerDown(e) {
    if (!this.floating.matches || e.button !== 0) {
      return;
    }

    // Stops the drag from selecting the title bar's text along the way.
    e.preventDefault();
    this.pin();

    const rect = this.element.getBoundingClientRect();
    this.grip = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    this.pointerId = e.pointerId;
    this.element.classList.add("is-dragging");

    // Capture keeps the events coming to the bar even when the cursor outruns
    // it — the old mousemove-on-document version dropped the window instead.
    this.handle.setPointerCapture?.(e.pointerId);
  }

  pointerMove(e) {
    if (this.pointerId === null || e.pointerId !== this.pointerId) {
      return;
    }

    const { width, height } = this.element.getBoundingClientRect();
    const left = clamp(
      e.clientX - this.grip.x,
      KEEP_VISIBLE - width,
      window.innerWidth - KEEP_VISIBLE
    );
    const top = clamp(
      e.clientY - this.grip.y,
      0,
      window.innerHeight - KEEP_VISIBLE
    );

    this.element.style.left = `${left}px`;
    this.element.style.top = `${top}px`;
  }

  pointerUp(e) {
    if (this.pointerId === null || e.pointerId !== this.pointerId) {
      return;
    }

    this.handle.releasePointerCapture?.(this.pointerId);
    this.pointerId = null;
    this.grip = null;
    this.element.classList.remove("is-dragging");
  }

  // The window is centred with `translate(-50%, -50%)`, which dragging would
  // have to keep compensating for. Convert that to real coordinates once, on
  // the first grab, and from then on left/top are the whole truth.
  pin() {
    if (this.pinned) {
      return;
    }

    const rect = this.element.getBoundingClientRect();
    this.element.style.transform = "none";
    this.element.style.left = `${rect.left}px`;
    this.element.style.top = `${rect.top}px`;
    this.pinned = true;
  }

  // Dropping below the breakpoint hands layout back to the stylesheet, so the
  // coordinates from a previous drag have to go with it.
  layoutChanged() {
    if (this.floating.matches || !this.pinned) {
      return;
    }

    this.element.style.transform = "";
    this.element.style.left = "";
    this.element.style.top = "";
    this.pinned = false;
  }

}
